import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { PeerReviewQualityTrendPoint } from '../../services/peerReviewAnalyticsService';

interface PeerReviewQualityTrendChartProps {
  points: PeerReviewQualityTrendPoint[];
  trendPeriod: 'day' | 'week' | 'month';
}

type ChartCoord = { x: number; y: number };

const CHART_HEIGHT = 340;
const SERIES_COLORS = {
  concur: '#22c55e',
  minorDiscrepancy: '#3b82f6',
  clinicallySignificantDiscrepancy: '#ef4444',
};

function clampChartY(y: number, top: number, bottom: number): number {
  return Math.max(top, Math.min(bottom, y));
}

function buildLinearLinePath(coords: ChartCoord[]): string {
  if (coords.length === 0) {
    return '';
  }
  return coords.map((coord, index) => `${index === 0 ? 'M' : 'L'} ${coord.x} ${coord.y}`).join(' ');
}

function buildAreaPath(coords: ChartCoord[], bottomY: number): string {
  if (coords.length === 0) {
    return '';
  }
  const line = buildLinearLinePath(coords);
  const last = coords.at(-1);
  const first = coords[0];
  if (!last || !first) {
    return '';
  }
  return `${line} L ${last.x} ${bottomY} L ${first.x} ${bottomY} Z`;
}

function getXAxisLabelIndices(pointCount: number, innerWidth: number): number[] {
  if (pointCount <= 0) {
    return [];
  }
  if (pointCount === 1) {
    return [0];
  }

  const minLabelSpacingPx = 56;
  const step = Math.max(
    1,
    Math.ceil((minLabelSpacingPx * (pointCount - 1)) / Math.max(innerWidth, 1))
  );

  const indices: number[] = [];
  for (let index = 0; index < pointCount; index += step) {
    indices.push(index);
  }

  const lastIndex = pointCount - 1;
  const prevIndex = indices.at(-1);
  if (prevIndex !== undefined && prevIndex !== lastIndex && lastIndex - prevIndex >= step) {
    indices.push(lastIndex);
  }

  return indices;
}

function computeYAxis(points: PeerReviewQualityTrendPoint[]): { yMax: number; yTicks: number[] } {
  const dataMax = points.reduce(
    (max, point) =>
      Math.max(max, point.concur, point.minorDiscrepancy, point.clinicallySignificantDiscrepancy),
    0
  );
  const yMax = Math.max(dataMax, 1);
  const step = Math.max(1, Math.ceil(yMax / 4));
  const yTicks: number[] = [];
  for (let tick = 0; tick <= yMax; tick += step) {
    yTicks.push(tick);
  }
  if (yTicks.at(-1) !== yMax) {
    yTicks.push(yMax);
  }
  return { yMax, yTicks: [...new Set(yTicks)].sort((a, b) => a - b) };
}

function trendPeriodAxisLabel(trendPeriod: 'day' | 'week' | 'month'): string {
  if (trendPeriod === 'month') {
    return 'Month';
  }
  if (trendPeriod === 'week') {
    return 'Week';
  }
  return 'Day';
}

export default function PeerReviewQualityTrendChart({
  points,
  trendPeriod,
}: Readonly<PeerReviewQualityTrendChartProps>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(Math.max(element.clientWidth, 320));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    globalThis.window?.addEventListener('resize', updateWidth);

    return () => {
      observer.disconnect();
      globalThis.window?.removeEventListener('resize', updateWidth);
    };
  }, []);

  const { yMax, yTicks } = useMemo(() => computeYAxis(points), [points]);

  const chart = useMemo(() => {
    const width = containerWidth || 320;
    const height = CHART_HEIGHT;
    const padding = { top: 16, right: 20, bottom: 64, left: 56 };
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;
    const bottomY = padding.top + innerH;
    const xStep = points.length > 1 ? innerW / (points.length - 1) : 0;
    const yScale = (value: number) => padding.top + innerH - (value / yMax) * innerH;

    const buildSeriesCoords = (key: keyof typeof SERIES_COLORS) =>
      points.map((point, index) => ({
        x: points.length === 1 ? padding.left + innerW / 2 : padding.left + index * xStep,
        y: clampChartY(yScale(point[key]), padding.top, bottomY),
      }));

    return {
      width,
      height,
      padding,
      innerH,
      innerW,
      bottomY,
      xLabelIndices: getXAxisLabelIndices(points.length, innerW),
      concurCoords: buildSeriesCoords('concur'),
      minorCoords: buildSeriesCoords('minorDiscrepancy'),
      csdCoords: buildSeriesCoords('clinicallySignificantDiscrepancy'),
    };
  }, [points, containerWidth, yMax]);

  const activePoint = hoveredIndex === null ? null : points[hoveredIndex];

  return (
    <div
      className="pra-chart-wrap"
      ref={wrapRef}
    >
      {containerWidth > 0 && (
        <svg
          className="pra-chart-svg"
          viewBox={`0 0 ${chart.width} ${chart.height}`}
          preserveAspectRatio="none"
          aria-labelledby="pra-quality-trend-chart-title"
        >
          <title id="pra-quality-trend-chart-title">Peer review quality trend chart</title>
          {yTicks.map(tick => {
            const y = chart.padding.top + chart.innerH - (tick / yMax) * chart.innerH;
            return (
              <g key={tick}>
                <line
                  className="pra-chart-grid-line"
                  x1={chart.padding.left}
                  y1={y}
                  x2={chart.width - chart.padding.right}
                  y2={y}
                />
                <text
                  className="pra-chart-axis-label"
                  x={chart.padding.left - 10}
                  y={y + 4}
                  textAnchor="end"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          <text
            className="pra-chart-axis-label pra-chart-axis-label--y"
            x={12}
            y={chart.padding.top + chart.innerH / 2}
            textAnchor="middle"
            transform={`rotate(-90, 12, ${chart.padding.top + chart.innerH / 2})`}
          >
            Review count
          </text>

          <text
            className="pra-chart-axis-label pra-chart-axis-label--x"
            x={chart.padding.left + chart.innerW / 2}
            y={chart.height - 12}
            textAnchor="middle"
          >
            {trendPeriodAxisLabel(trendPeriod)}
          </text>

          <path
            className="pra-chart-area pra-chart-area--concur"
            d={buildAreaPath(chart.concurCoords, chart.bottomY)}
            fill={SERIES_COLORS.concur}
            fillOpacity={0.12}
          />
          <path
            className="pra-chart-area pra-chart-area--minor"
            d={buildAreaPath(chart.minorCoords, chart.bottomY)}
            fill={SERIES_COLORS.minorDiscrepancy}
            fillOpacity={0.12}
          />
          <path
            className="pra-chart-area pra-chart-area--csd"
            d={buildAreaPath(chart.csdCoords, chart.bottomY)}
            fill={SERIES_COLORS.clinicallySignificantDiscrepancy}
            fillOpacity={0.12}
          />

          <path
            className="pra-chart-line pra-chart-line--concur"
            d={buildLinearLinePath(chart.concurCoords)}
            stroke={SERIES_COLORS.concur}
          />
          <path
            className="pra-chart-line pra-chart-line--minor"
            d={buildLinearLinePath(chart.minorCoords)}
            stroke={SERIES_COLORS.minorDiscrepancy}
          />
          <path
            className="pra-chart-line pra-chart-line--csd"
            d={buildLinearLinePath(chart.csdCoords)}
            stroke={SERIES_COLORS.clinicallySignificantDiscrepancy}
          />

          {chart.xLabelIndices.map(index => {
            const coord = chart.concurCoords[index];
            if (!coord) {
              return null;
            }
            return (
              <text
                key={`x-${index}`}
                className="pra-chart-axis-label pra-chart-axis-label--tick"
                x={coord.x}
                y={chart.height - 34}
                textAnchor="middle"
              >
                {points[index]?.label}
              </text>
            );
          })}

          <rect
            x={chart.padding.left}
            y={chart.padding.top}
            width={chart.innerW}
            height={chart.innerH}
            fill="transparent"
            onMouseMove={event => {
              const svg = event.currentTarget.ownerSVGElement;
              if (!svg || chart.width <= 0 || points.length === 0) {
                return;
              }
              const rect = svg.getBoundingClientRect();
              const svgX = ((event.clientX - rect.left) / rect.width) * chart.width;
              let nearestIndex = 0;
              let nearestDistance = Number.POSITIVE_INFINITY;
              chart.concurCoords.forEach((coord, index) => {
                const distance = Math.abs(coord.x - svgX);
                if (distance < nearestDistance) {
                  nearestDistance = distance;
                  nearestIndex = index;
                }
              });
              setHoveredIndex(nearestIndex);
            }}
            onMouseLeave={() => setHoveredIndex(null)}
          />
        </svg>
      )}

      {activePoint && (
        <div
          className="pra-chart-tooltip"
          role="tooltip"
        >
          <div className="pra-chart-tooltip-date">{activePoint.label}</div>
          <div className="pra-chart-tooltip-row">
            <span className="pra-chart-tooltip-item">
              <span
                className="pra-chart-tooltip-dot"
                style={{ background: SERIES_COLORS.concur }}
              />
              Concur {activePoint.concur}
            </span>
          </div>
          <div className="pra-chart-tooltip-row">
            <span className="pra-chart-tooltip-item">
              <span
                className="pra-chart-tooltip-dot"
                style={{ background: SERIES_COLORS.minorDiscrepancy }}
              />
              Minor discrepancy {activePoint.minorDiscrepancy}
            </span>
          </div>
          <div className="pra-chart-tooltip-row">
            <span className="pra-chart-tooltip-item">
              <span
                className="pra-chart-tooltip-dot"
                style={{ background: SERIES_COLORS.clinicallySignificantDiscrepancy }}
              />
              Clinically significant discrepancy {activePoint.clinicallySignificantDiscrepancy}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
